import { Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, take } from 'rxjs';
import { ProformViewStates } from '../../../dtos/proforms.dto';
import { GlaiService } from '../../../services/glai/glai.service';
import { ProformsSubmissionService } from '../../../services/proforms/proforms-submission.service';
import {
  ProformFieldTypes,
  ProFormsQuestionAPIResponse,
} from '../../../types/responses/proforms.responses';

@Component({
  selector: 'app-proforms-question-answer-text',
  standalone: false,
  templateUrl: './proforms-question-answer-text.component.html',
  styleUrl: './proforms-question-answer-text.component.scss',
})
export class ProformsQuestionAnswerTextComponent implements OnInit {
  @Input() question: ProFormsQuestionAPIResponse;

  @Input() viewState: ProformViewStates = ProformViewStates.PREVIEW;

  @Input() isObservationSession = false;

  @Input() formId: number;

  FieldTypes = ProformFieldTypes;

  viewStates = ProformViewStates;

  textControl: FormControl;

  textareaControl: FormControl;

  debounceTime = 500;

  smartResponsePreview: string | null = null;

  smartResponseLoading = false;

  smartResponseError: string | null = null;

  constructor(
    private proformsSubmissionService: ProformsSubmissionService,
    private glaiService: GlaiService
  ) {}

  ngOnInit(): void {
    this.textControl = new FormControl({
      value: '',
      disabled:
        this.viewState === this.viewStates.SUBMITTED ||
        this.viewState === this.viewStates.GRADED,
    });

    this.textareaControl = new FormControl({
      value: '',
      disabled:
        this.viewState === this.viewStates.SUBMITTED ||
        this.viewState === this.viewStates.GRADED,
    });

    if (this.question) {
      if (
        this.viewState === this.viewStates.SUBMITTED ||
        this.viewState === this.viewStates.GRADED
      ) {
        this.proformsSubmissionService.currentSubmission.subscribe(
          (submission) => {
            if (submission) {
              if (submission.answers) {
                const answer = submission.answers.find(
                  (a) => a.proform_form_question_id === this.question.id
                );
                if (answer) {
                  this.setValue(answer.text_answer);
                }
              }
            }
          }
        );
      } else if (this.viewState === this.viewStates.EDIT) {
        this.proformsSubmissionService.currentSubmission.subscribe(
          (submission) => {
            if (submission) {
              if (submission.answers) {
                const answer = submission.answers.find(
                  (a) => a.proform_form_question_id === this.question.id
                );
                if (answer) {
                  this.setValue(answer.text_answer);
                }
              }
            }
          }
        );
        // Load in form values from local storage if there are any
        this.proformsSubmissionService.newSubmission
          .pipe(take(1))
          .subscribe((submission) => {
            if (submission) {
              if (submission.answers) {
                const answer = submission.answers.find(
                  (a) => a.proform_form_question_id === this.question.id
                );
                if (answer?.text_answer) {
                  this.setValue(answer.text_answer);
                }
              }
            }
          });
        // Listeners for updating payload values
        if (this.question.field_type === this.FieldTypes.TEXT) {
          this.textControl.valueChanges
            .pipe(debounceTime(this.debounceTime))
            .subscribe((value) => {
              this.proformsSubmissionService.addAnswerToPayload(
                {
                  proform_form_question_id: this.question.id,
                  text_answer: value,
                },
                this.question.field_type
              );
            });
        } else {
          this.textareaControl.valueChanges
            .pipe(debounceTime(this.debounceTime))
            .subscribe((value) => {
              this.smartResponsePreview = null;
              this.proformsSubmissionService.addAnswerToPayload(
                {
                  proform_form_question_id: this.question.id,
                  text_answer: value,
                },
                this.question.field_type
              );
            });
        }
      }
    }
  }

  setValue(answer: string): void {
    if (this.question.field_type === this.FieldTypes.TEXT) {
      this.textControl.setValue(answer, { emitEvent: false });
    } else {
      this.textareaControl.setValue(answer, { emitEvent: false });
    }
  }

  generateSmartResponse(): void {
    this.smartResponseLoading = true;
    this.smartResponsePreview = null;
    this.smartResponseError = null;

    this.proformsSubmissionService.newSubmission
      .pipe(take(1))
      .subscribe((submission) => {
        this.glaiService
          .generateProformSmartResponse({
            question_id: this.question.id,
            form_id: this.formId,
            answers: submission?.answers ?? [],
          })
          .subscribe({
            next: (result) => {
              this.smartResponsePreview = result.response;
              this.smartResponseLoading = false;
            },
            error: (error) => {
              this.smartResponseLoading = false;
              this.smartResponseError =
                'There was an error generating a smart response. Please try again.';
              // eslint-disable-next-line no-console
              console.error(error);
            },
          });
      });
  }

  acceptSmartResponse(): void {
    if (this.smartResponsePreview) {
      this.textareaControl.setValue(this.smartResponsePreview);
      this.smartResponsePreview = null;
    }
  }

  dismissSmartResponse(): void {
    this.smartResponsePreview = null;
  }
}
